import { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { isNil } from 'lodash-es';

import { IStore } from '@rbi-ctg/store';
import { GetRestaurantDocument, IGetRestaurantQuery } from 'generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { ServiceMode } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { Store, StoreState } from 'state/store/hooks';
import { isMobileOrderingAvailable, isRestaurantOpen } from 'utils/restaurant';

export type ServiceModeStatus = {
  [key in ServiceMode]: {
    /**
     * store is available in MDM
     */
    capable: boolean;
    /**
     * store is available if store hours are within service mode availability
     * and the store supports this service mode type
     */
    available: boolean;
    /**
     * state is disabled based on flag from LD, separate from store's availability
     */
    disabled: boolean;
    /**
     * store hours are open within service mode availability
     */
    open?: boolean;
  };
};

export const useServiceModeStatus = (store: StoreState | Store) => {
  const { query } = useApolloClient();
  const logger = useLogger();
  const { serviceMode } = useServiceModeContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const storeSelection2_0Enabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0);

  // Move these up in order to check for null (meaning LD service is down) or undefined
  // FYI - use ENABLE_* flags to avoid this necessity as null values will correctly disable the option
  const driveThruDisabled = useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const dineInDisabled = useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const takeOutDisabled = useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);

  const mdmStore = (store !== null ? store : {}) as IStore;

  const serviceModeStatus: ServiceModeStatus = {
    CATERING_DELIVERY: {
      capable: !!mdmStore.hasDelivery && !!mdmStore.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY)
    },
    CATERING_PICKUP: {
      capable: !!mdmStore.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING)
    },
    CURBSIDE: {
      capable: !!mdmStore.hasCurbside,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CURBSIDE),
      open: false
    },
    DELIVERY: {
      capable: !!mdmStore.hasDelivery,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_DELIVERY),
      open: false
    },
    DRIVE_THRU: {
      capable: !!mdmStore.hasDriveThru,
      available: false,
      disabled: isNil(driveThruDisabled) || driveThruDisabled,
      open: false
    },
    EAT_IN: {
      capable: !!mdmStore.hasDineIn,
      available: false,
      disabled: isNil(dineInDisabled) || dineInDisabled,
      open: false
    },
    TAKEOUT: {
      capable: !!mdmStore.hasTakeOut,
      available: false,
      disabled: isNil(takeOutDisabled) || takeOutDisabled,
      open: false
    }
  };

  let allServiceModesUnavailable = true;

  // performing an explicit check against `false` because some stores do not have these properties configured
  // therefore hasSelection and isRestaurantPosOnline can be undefined even though the store is available
  if (
    store !== null &&
    (!('hasSelection' in store) || ('hasSelection' in store && store.hasSelection !== false))
  ) {
    const curbsideOpen = isRestaurantOpen(store.curbsideHours);
    const deliveryOpen = isRestaurantOpen(store.deliveryHours);
    const diningRoomOpen = isRestaurantOpen(store.diningRoomHours);
    const driveThruOpen = isRestaurantOpen(store.driveThruHours);

    /** Need to know if service mode is open for store selection 2.0 */
    serviceModeStatus.DELIVERY.open = deliveryOpen;
    serviceModeStatus.EAT_IN.open = diningRoomOpen;
    serviceModeStatus.TAKEOUT.open = diningRoomOpen;
    serviceModeStatus.CURBSIDE.open = curbsideOpen;
    serviceModeStatus.DRIVE_THRU.open = driveThruOpen;

    serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
    serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
    serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable && deliveryOpen;
    serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable && diningRoomOpen;
    serviceModeStatus.CURBSIDE.available = serviceModeStatus.CURBSIDE.capable && curbsideOpen;
    serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable && diningRoomOpen;
    serviceModeStatus.DRIVE_THRU.available = serviceModeStatus.DRIVE_THRU.capable && driveThruOpen;

    allServiceModesUnavailable = Object.values(serviceModeStatus).every(
      ({ available, disabled }) => !available || disabled
    );
  }

  const checkIfRestaurantPosIsOnline = useCallback(
    /* eslint-disable-next-line */
    async (storeId: string) => {
      try {
        if (!storeId) {
          return false;
        }

        if (!enableOrdering) {
          return true;
        }

        const { data, errors } = await query<IGetRestaurantQuery>({
          query: GetRestaurantDocument,
          variables: { storeId },
          fetchPolicy: 'network-only'
        });
        // optimistically return true if this fails due to a network error
        return errors ? true : data?.restaurant?.available;
      } catch (error) {
        logger.warn({
          error,
          storeId,
          message: 'Failed to retrieve restaurant availability'
        });
        // optimistically return true if the query throws
        return true;
      }
    },
    [enableOrdering, logger, query]
  );

  const isRestaurantAvailable = useCallback(
    async (restaurantData: IStore | StoreState) => {
      const mobileOrderingEnabled = isMobileOrderingAvailable(restaurantData as IStore);
      const { number: storeId } = restaurantData as IStore;

      const isRestaurantPosAvailable = await checkIfRestaurantPosIsOnline(storeId);

      const open =
        !serviceMode ||
        [ServiceMode.DELIVERY, ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
          serviceMode
        ) ||
        (serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled);

      return open && isRestaurantPosAvailable && mobileOrderingEnabled;
    },
    [checkIfRestaurantPosIsOnline, serviceMode, serviceModeStatus]
  );

  /** Determines whether a restaurant can be selected */
  const restaurantCanBeSelected = useMemo(() => {
    // Store selection 2.0: any store can be selected
    if (storeSelection2_0Enabled && store) {
      return true;
    }

    const curbsideInAvailable =
      serviceModeStatus.CURBSIDE.available && !serviceModeStatus.CURBSIDE.disabled;
    const dineInAvailable =
      serviceModeStatus.EAT_IN.available && !serviceModeStatus.EAT_IN.disabled;
    const driveThruAvailable =
      serviceModeStatus.DRIVE_THRU.available && !serviceModeStatus.DRIVE_THRU.disabled;
    const takeoutAvailable =
      serviceModeStatus.TAKEOUT.available && !serviceModeStatus.TAKEOUT.disabled;

    // Store selection 1.0: store can only be selected if there is an available service mode
    return (
      isMobileOrderingAvailable &&
      (curbsideInAvailable || dineInAvailable || driveThruAvailable || takeoutAvailable)
    );
  }, [serviceModeStatus, store, storeSelection2_0Enabled]);

  return {
    serviceModeStatus,
    allServiceModesUnavailable,
    checkIfRestaurantPosIsOnline,
    isRestaurantAvailable,
    restaurantCanBeSelected
  };
};
